import { type ReactNode } from "react";
import { NavDropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { css, cx } from "../../../../styled-system/css";
import { ProgressBar } from "react-aria-components";
import { ImSpinner2 } from "react-icons/im";

// Named types for clarity
type SelectableItem = {
  value: string;
  label: string;
  isDisabled?: boolean;
  icon?: ReactNode;
};

type HeaderItem = {
  header: true;
  label: string;
};

type ActionItem = {
  id: string;
  onAction: () => void;
  label: ReactNode;
  icon?: ReactNode;
};

type NavFilterItem = SelectableItem | HeaderItem | ActionItem;

// Type guards
const isSelectableItem = (item: NavFilterItem): item is SelectableItem => "value" in item;

const isActionItem = (item: NavFilterItem): item is ActionItem => "onAction" in item;

const isHeaderItem = (item: NavFilterItem): item is HeaderItem => "header" in item;

interface Props {
  mobileSidebarItem?: boolean;
  selectedKey: string;
  onSelectionChange: (key: string) => void;
  items: NavFilterItem[];
  isLoading?: boolean;
}

// Styles
const dropdownItemPadding = css({ padding: "0.5rem" });
const headerItemStyles = css({ marginTop: "1rem", padding: "0.5rem", fontSize: "20px" });
const spinnerStyles = css({ animation: "spin" });
const progressBarStyles = css({ display: "contents" });

const mobileContainerStyles = css({
  display: "contents",
  zIndex: 3,
  position: "relative",
  margin: "1rem 1rem",
  fontSize: "medium",
  boxShadow: "0 0 0 0 rgba(0,0,0,0.5)",
});

const desktopDropdownStyles = css({
  zIndex: 3,
  position: "relative",
  margin: "0",
  fontSize: "medium",
  boxShadow: "0 0 0 0 rgba(0,0,0,0.5)",
});

function MobileNavFilter({
  items,
  selectedKey,
  onNavClick,
}: {
  items: NavFilterItem[];
  selectedKey: string;
  onNavClick: (key: string) => void;
}) {
  const filteredItems = items.filter((item) => {
    if (isActionItem(item)) return true;
    if (isHeaderItem(item)) return false;
    if (isSelectableItem(item)) {
      return !item.isDisabled && item.value !== selectedKey;
    }
    return false;
  });

  return (
    <div className={mobileContainerStyles}>
      {/* In mobile sidebar, only show options that can be selected to avoid overflow */}
      {filteredItems.map((item) => {
        if (isActionItem(item)) {
          return (
            <NavDropdown.Item key={item.id} onClick={() => item.onAction()} className="text-dark-purple">
              {item.icon}
              {item.label}
            </NavDropdown.Item>
          );
        }
        if (isSelectableItem(item)) {
          return (
            <NavDropdown.Item
              key={item.value}
              disabled={item.isDisabled}
              onClick={() => onNavClick(item.value)}
              id={item.value}
              className="text-dark-purple"
            >
              {item.icon}
              {item.label}
            </NavDropdown.Item>
          );
        }
        return null;
      })}
    </div>
  );
}

function DesktopNavFilter({
  items,
  selectedItem,
  isLoading,
  onNavClick,
}: {
  items: NavFilterItem[];
  selectedItem: SelectableItem | undefined;
  isLoading?: boolean;
  onNavClick: (key: string) => void;
}) {
  const { t } = useTranslation(["common"]);
  return (
    <NavDropdown
      title={
        <>
          {selectedItem?.icon}
          <span>{selectedItem?.label}</span>
          {isLoading && (
            <ProgressBar aria-label={t(($) => $.common["Loading..."])} isIndeterminate className={progressBarStyles}>
              <ImSpinner2 className={spinnerStyles} />
            </ProgressBar>
          )}
        </>
      }
      disabled={isLoading}
      className={cx("outline", desktopDropdownStyles)}
      aria-label={t(($) => $.common.navFilter["Navigation filter"])}
    >
      {items.map((item) => {
        if (isActionItem(item)) {
          return (
            <NavDropdown.Item key={item.id} onClick={() => item.onAction()} className={dropdownItemPadding}>
              {item.icon}
              {item.label}
            </NavDropdown.Item>
          );
        }
        if (isHeaderItem(item)) {
          return (
            <NavDropdown.Item key={`header-${item.label}`} disabled className={headerItemStyles}>
              {item.label}
            </NavDropdown.Item>
          );
        }
        if (isSelectableItem(item)) {
          return (
            <NavDropdown.Item
              key={item.value}
              disabled={item.isDisabled}
              onClick={() => onNavClick(item.value)}
              id={item.value}
              className={dropdownItemPadding}
            >
              {item.icon}
              {item.label}
            </NavDropdown.Item>
          );
        }
        return null;
      })}
    </NavDropdown>
  );
}

export function NavFilterTile({ selectedKey, onSelectionChange, items, mobileSidebarItem, isLoading }: Props) {
  const onNavClick = (key: string) => {
    onSelectionChange(key);
    if (mobileSidebarItem) {
      document.documentElement.classList.toggle("nav-open");
    }
  };

  const selectedItem = items.find(
    (item): item is SelectableItem => isSelectableItem(item) && item.value === selectedKey,
  );

  return mobileSidebarItem ?
      <MobileNavFilter items={items} selectedKey={selectedKey} onNavClick={onNavClick} />
    : <DesktopNavFilter items={items} selectedItem={selectedItem} isLoading={isLoading} onNavClick={onNavClick} />;
}
